import { useCallback, useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import {
  ApiError,
  deleteStaff,
  inviteStaff,
  listStaff,
  resendInvite,
  updateStaff,
} from "../lib/api";
import type { StaffUser } from "../lib/api";
import { formatWhen } from "../lib/format";
import { TrashIcon } from "../components/Icons";

/** Cognito's status vocabulary, in words an office manager can act on. */
function describe(user: StaffUser): { label: string; tone: string; hint: string } {
  if (!user.enabled) {
    return { label: "Disabled", tone: "bad", hint: "Cannot sign in. History is kept." };
  }
  if (user.status === "FORCE_CHANGE_PASSWORD") {
    return {
      label: "Invited",
      tone: "",
      hint: "Sent an invitation but has not signed in yet. Temporary passwords expire after 3 days.",
    };
  }
  if (user.status === "RESET_REQUIRED") {
    return { label: "Reset required", tone: "", hint: "Must set a new password to sign in." };
  }
  return { label: "Active", tone: "ok", hint: "" };
}

/** Role names are Cognito group names; these are how staff refer to them. */
const ROLE_LABELS: Record<string, string> = {
  Admin: "Admin",
  DirectorApproval: "Director Approval",
  Reviewer: "Reviewer",
  Preparer: "Preparer",
};

const label = (role: string) => ROLE_LABELS[role] ?? role;

function RoleChecks(props: {
  roles: string[];
  selected: string[];
  onChange: (roles: string[]) => void;
  disabled?: boolean;
  name: string;
}) {
  const { roles, selected, onChange, disabled, name } = props;
  return (
    <div className="roles-picker">
      {roles.map((role) => (
        <label key={role} className="role-check">
          <input
            type="checkbox"
            name={`${name}-${role}`}
            disabled={disabled}
            checked={selected.includes(role)}
            onChange={(changeEvent) =>
              onChange(
                changeEvent.target.checked
                  ? [...selected, role]
                  : selected.filter((r) => r !== role),
              )
            }
          />
          {label(role)}
        </label>
      ))}
    </div>
  );
}

export function UsersPage() {
  const auth = useAuth();
  const token = auth.user?.access_token;
  const mySub = auth.user?.profile?.sub;

  const [users, setUsers] = useState<StaffUser[] | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const data = await listStaff(token);
      setUsers(data.users);
      setRoles(data.roles);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load staff accounts");
    }
  }, [token]);

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    void refresh();
  }, [refresh]);

  const adminCount = (users ?? []).filter(
    (u) => u.enabled && u.roles.includes("Admin"),
  ).length;

  return (
    <section>
      <div className="section-head">
        <h2>Staff</h2>
      </div>

      <p className="muted intro">
        Accounts are managed here rather than in the AWS console. An invitation goes to the
        address by email with a temporary password &mdash; Casa never sees it, and cannot
        set one.
      </p>

      {notice && (
        <div className="notice success">
          {notice}
          <button className="link" onClick={() => setNotice(null)}>
            Dismiss
          </button>
        </div>
      )}
      {error && <div className="notice error">{error}</div>}

      <InviteStaff
        roles={roles}
        onDone={async (message) => {
          setNotice(message);
          await refresh();
        }}
        onError={setError}
      />

      {users === null ? (
        <p className="muted">Loading…</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Status</th>
              <th>Roles</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <StaffRow
                key={user.username}
                user={user}
                roles={roles}
                isSelf={user.username === mySub || undefined}
                lastAdmin={user.roles.includes("Admin") && adminCount <= 1}
                onDone={async (message) => {
                  if (message) setNotice(message);
                  await refresh();
                }}
                onError={setError}
              />
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function StaffRow(props: {
  user: StaffUser;
  roles: string[];
  isSelf?: boolean;
  lastAdmin: boolean;
  onDone: (message?: string) => Promise<void>;
  onError: (message: string) => void;
}) {
  const auth = useAuth();
  const { user, roles, isSelf, lastAdmin } = props;
  const [selected, setSelected] = useState<string[]>(user.roles);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const status = describe(user);
  const dirty =
    selected.length !== user.roles.length ||
    selected.some((role) => !user.roles.includes(role));

  async function run(action: () => Promise<void>, message?: string) {
    const token = auth.user?.access_token;
    if (!token) return;
    setBusy(true);
    try {
      await action();
      await props.onDone(message);
    } catch (err) {
      // A 409 here is a lockout guard, and its message is the useful part.
      props.onError(err instanceof ApiError ? err.message : "That change did not apply");
    } finally {
      setBusy(false);
    }
  }

  const token = auth.user?.access_token ?? "";

  return (
    <>
      <tr>
        <td>
          <strong>{user.email}</strong>
          {isSelf && <span className="tag">you</span>}
          {user.created_at > 0 && (
            <div className="muted">Added {formatWhen(user.created_at)}</div>
          )}
        </td>
        <td>
          <span className={`tag ${status.tone}`}>{status.label}</span>
          {status.hint && <div className="muted">{status.hint}</div>}
        </td>
        <td>
          <RoleChecks
            name={user.username}
            roles={roles}
            selected={selected}
            disabled={busy}
            onChange={setSelected}
          />
          {user.roles.length === 0 && !dirty && (
            <div className="muted">No role &mdash; this account cannot do anything yet.</div>
          )}
          {dirty && (
            <div className="row">
              <button
                className="primary"
                disabled={busy}
                onClick={() =>
                  void run(
                    async () => {
                      await updateStaff(token, user.username, { roles: selected });
                    },
                    `${user.email}'s roles were updated.`,
                  )
                }
              >
                {busy ? "Saving…" : "Save roles"}
              </button>
              <button className="link" onClick={() => setSelected(user.roles)}>
                Cancel
              </button>
            </div>
          )}
        </td>
        <td className="actions">
          {user.status === "FORCE_CHANGE_PASSWORD" && user.enabled && (
            <button
              disabled={busy}
              title="Send the invitation again with a fresh temporary password"
              onClick={() =>
                void run(
                  async () => {
                    await resendInvite(token, user.username);
                  },
                  `Invitation resent to ${user.email}.`,
                )
              }
            >
              Resend invite
            </button>
          )}
          <button
            disabled={busy || isSelf || (user.enabled && lastAdmin)}
            title={
              isSelf
                ? "You cannot disable your own account"
                : user.enabled && lastAdmin
                  ? "This is the only administrator left"
                  : user.enabled
                    ? "Stop this account signing in, keeping its history"
                    : "Let this account sign in again"
            }
            onClick={() =>
              void run(
                async () => {
                  await updateStaff(token, user.username, { enabled: !user.enabled });
                },
                `${user.email} was ${user.enabled ? "disabled" : "re-enabled"}.`,
              )
            }
          >
            {user.enabled ? "Disable" : "Enable"}
          </button>
          <button
            className="icon danger"
            aria-label={`Delete ${user.email}`}
            aria-expanded={confirming}
            disabled={busy || isSelf || lastAdmin}
            title={
              isSelf
                ? "You cannot delete your own account"
                : lastAdmin
                  ? "This is the only administrator left"
                  : "Delete this account permanently"
            }
            onClick={() => setConfirming(!confirming)}
          >
            <TrashIcon />
          </button>
        </td>
      </tr>

      {confirming && (
        <tr className="drawer">
          <td colSpan={4}>
            <div className="confirm-delete">
              <p>
                <strong>Delete {user.email}?</strong> Disabling is usually the better
                choice for someone who has left &mdash; it stops them signing in while
                keeping their name attached to the workpapers they prepared. Deleting
                removes the account, so past actions can no longer be traced back to a
                person.
              </p>
              <button
                className="danger"
                disabled={busy}
                onClick={() =>
                  void run(
                    async () => {
                      await deleteStaff(token, user.username);
                    },
                    `${user.email} was deleted.`,
                  )
                }
              >
                {busy ? "Deleting…" : "Delete anyway"}
              </button>
              <button className="link" onClick={() => setConfirming(false)}>
                Cancel
              </button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function InviteStaff(props: {
  roles: string[];
  onDone: (message: string) => Promise<void>;
  onError: (message: string) => void;
}) {
  const auth = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);

  async function submit(submitEvent: React.FormEvent) {
    submitEvent.preventDefault();
    const token = auth.user?.access_token;
    if (!token) return;

    setSaving(true);
    try {
      await inviteStaff(token, email, selected);
      setEmail("");
      setSelected([]);
      setOpen(false);
      setFieldError(null);
      await props.onDone(`Invitation sent to ${email}.`);
    } catch (err) {
      setFieldError(
        err instanceof ApiError ? err.message : "Could not create that account",
      );
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <p>
        <button className="primary" onClick={() => setOpen(true)}>
          Invite staff member
        </button>
      </p>
    );
  }

  return (
    <form className="add-client invite" onSubmit={(formEvent) => void submit(formEvent)}>
      <input
        aria-label="Email address"
        type="email"
        placeholder="name@casaadvisory.com.au"
        required
        value={email}
        onChange={(changeEvent) => setEmail(changeEvent.target.value)}
      />
      <RoleChecks
        name="invite"
        roles={props.roles}
        selected={selected}
        onChange={setSelected}
        disabled={saving}
      />
      <button className="primary" type="submit" disabled={saving}>
        {saving ? "Sending…" : "Send invitation"}
      </button>
      <button type="button" className="link" onClick={() => setOpen(false)}>
        Cancel
      </button>
      {selected.length === 0 && (
        <span className="muted">
          No role selected. The account will be created but cannot do anything until one
          is given.
        </span>
      )}
      {fieldError && <span className="field-error">{fieldError}</span>}
    </form>
  );
}
